/**
 * The file preview overlay every module's Files list opens: images, PDFs, plain text, video and
 * audio show in place; anything else says so and offers the download. Reads go through the OS
 * file route, so the preview is exactly as authorized as a download.
 */
import { Download, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { fileExtension, fileUrl, previewKind } from '../lib/files';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './dialog';

export interface PreviewableFile {
  id: string;
  name: string;
  contentType: string;
}

/** The extension badge used by file rows and the preview header. */
export function FileBadge({ name, className }: { name: string; className?: string }) {
  return (
    <span
      className={cn(
        'grid size-9 shrink-0 place-items-center rounded-lg border border-[#E3D9C6] bg-[#F9F5ED] text-[10px] font-semibold tracking-[0.04em] text-foreground',
        className,
      )}
      aria-hidden
    >
      {fileExtension(name)}
    </span>
  );
}

export function FilePreview({
  file,
  meta,
  onClose,
}: {
  /** The file to show; null keeps the overlay closed. */
  file: PreviewableFile | null;
  /** The line under the name: "64 KB · Dev Leader · 12 Oct, 14:05". */
  meta?: string;
  onClose: () => void;
}) {
  const kind = file ? previewKind(file.contentType) : 'none';
  const src = file ? fileUrl(file.id) : '';
  return (
    <Dialog open={file !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        showCloseButton={false}
        overlayClassName="bg-[rgba(17,17,17,0.72)]"
        className="flex h-[calc(100vh-48px)] max-h-[900px] w-[calc(100vw-48px)] max-w-[1200px] flex-col gap-0 overflow-hidden rounded-2xl border-0 p-0 sm:max-w-[1200px] sm:rounded-2xl"
      >
        {file && (
          <>
            <div className="flex items-center gap-3 border-b py-3.5 pl-5 pr-4">
              <FileBadge name={file.name} />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <DialogTitle className="truncate text-base font-semibold">{file.name}</DialogTitle>
                <DialogDescription className="truncate text-xs text-muted-foreground">
                  {meta ?? file.contentType}
                </DialogDescription>
              </div>
              <a
                href={fileUrl(file.id, { download: true })}
                className="flex h-9 items-center gap-1.5 whitespace-nowrap rounded-[9px] border bg-background px-3.5 text-sm text-foreground no-underline hover:no-underline"
              >
                <Download className="size-3.5" strokeWidth={1.75} />
                Download
              </a>
              <button
                type="button"
                title="Close"
                aria-label="Close"
                onClick={onClose}
                className="grid size-9 place-items-center rounded-[9px] border bg-background"
              >
                <X className="size-4" strokeWidth={1.75} />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-muted">
              {kind === 'image' && (
                <img
                  src={src}
                  alt={file.name}
                  className="block max-h-full max-w-full object-contain"
                />
              )}
              {kind === 'video' && (
                <video src={src} controls className="max-h-full max-w-full">
                  <track kind="captions" />
                </video>
              )}
              {kind === 'audio' && <audio src={src} controls />}
              {kind === 'frame' && (
                <iframe
                  src={src}
                  title={file.name}
                  className="size-full border-0 bg-white [color-scheme:light]"
                />
              )}
              {kind === 'none' && (
                <div className="flex flex-col items-center gap-3 p-6 text-center">
                  <span className="text-[17px] font-semibold">No preview for this file type</span>
                  <span className="text-sm text-muted-foreground">
                    Images, PDFs, text, video and audio preview here. Download to open anything
                    else.
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
